import type { DailyMetrics, Grouping } from '../types';

export interface BucketedMetrics {
  key: string;
  label: string;
  // Cumulative (last day's value within the bucket)
  totalUsers: number;
  usersOnboarded: number;
  usersWithFirstArticle: number;
  usersWithFirstDeposit: number;
  usersWithFirstTranslation: number;
  totalArticles: number;
  totalBalanceCents: number;
  totalDepositCents: number;
  totalSpendCents: number;
  // Summed deltas across the bucket
  newSignups: number;
  dailyOnboarded: number;
  dailyFirstArticle: number;
  dailyFirstDeposit: number;
  dailyFirstTranslation: number;
  dailyArticlesCreated: number;
  dailyAiArticles: number;
  dailyImportArticles: number;
  dailyDepositCents: number;
  dailySpendCents: number;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseUtc(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00Z`);
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfIsoWeek(dateStr: string): string {
  const d = parseUtc(dateStr);
  const dow = d.getUTCDay();
  const offsetToMonday = (dow + 6) % 7;
  d.setUTCDate(d.getUTCDate() - offsetToMonday);
  return isoDate(d);
}

export function bucketKey(date: string, grouping: Grouping): string {
  if (grouping === 'day') return date;
  if (grouping === 'month') return date.slice(0, 7);
  return startOfIsoWeek(date);
}

export function bucketLabel(key: string, grouping: Grouping): string {
  if (grouping === 'day') return key.slice(5);
  if (grouping === 'week') return key.slice(5);
  const [year, month] = key.split('-');
  return `${MONTHS_SHORT[parseInt(month, 10) - 1]} ${year.slice(2)}`;
}

export function groupMetrics(history: DailyMetrics[], grouping: Grouping): BucketedMetrics[] {
  if (history.length === 0) return [];

  const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
  const buckets = new Map<string, BucketedMetrics>();

  for (const row of sorted) {
    const key = bucketKey(row.date, grouping);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        label: bucketLabel(key, grouping),
        totalUsers: 0,
        usersOnboarded: 0,
        usersWithFirstArticle: 0,
        usersWithFirstDeposit: 0,
        usersWithFirstTranslation: 0,
        totalArticles: 0,
        totalBalanceCents: 0,
        totalDepositCents: 0,
        totalSpendCents: 0,
        newSignups: 0,
        dailyOnboarded: 0,
        dailyFirstArticle: 0,
        dailyFirstDeposit: 0,
        dailyFirstTranslation: 0,
        dailyArticlesCreated: 0,
        dailyAiArticles: 0,
        dailyImportArticles: 0,
        dailyDepositCents: 0,
        dailySpendCents: 0,
      };
      buckets.set(key, bucket);
    }

    // Cumulative fields: last write wins (sorted ascending so the final
    // day in the bucket overwrites earlier values).
    bucket.totalUsers = row.totalUsers;
    bucket.usersOnboarded = row.usersOnboarded;
    bucket.usersWithFirstArticle = row.usersWithFirstArticle;
    bucket.usersWithFirstDeposit = row.usersWithFirstDeposit;
    bucket.usersWithFirstTranslation = row.usersWithFirstTranslation;
    bucket.totalArticles = row.totalArticles;
    bucket.totalBalanceCents = row.totalBalanceCents;
    bucket.totalDepositCents = row.totalDepositCents;
    bucket.totalSpendCents = row.totalSpendCents;

    // Delta fields: sum across the bucket.
    bucket.newSignups += row.newSignups;
    bucket.dailyOnboarded += row.dailyOnboarded;
    bucket.dailyFirstArticle += row.dailyFirstArticle;
    bucket.dailyFirstDeposit += row.dailyFirstDeposit;
    bucket.dailyFirstTranslation += row.dailyFirstTranslation;
    bucket.dailyArticlesCreated += row.dailyArticlesCreated;
    bucket.dailyAiArticles += row.dailyAiArticles;
    bucket.dailyImportArticles += row.dailyImportArticles;
    bucket.dailyDepositCents += row.dailyDepositCents;
    bucket.dailySpendCents += row.dailySpendCents;
  }

  return Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key));
}
